/**
 * Permisos de UN usuario en UNA empresa (spec 2026-10-07 §4).
 *
 * Escribe la matriz de empresa folders/{customerId}/users/{uid}: lo que el
 * usuario tiene en la empresa vale en TODAS sus carpetas, salvo las que se le
 * restrinjan (panel «Restringir acceso»). Puede quedar por debajo o por encima
 * de su rol (el rol es solo el punto de partida, lib/role_seed). También sirve
 * para el personal global: se le reduce sin vincularlo.
 *
 * Jerarquía (lib/rank): solo se ajusta a quien tiene MENOS rango. El rol del
 * objetivo sale del espejo público usuarios_rol (lib/role_mirror, con su
 * alcance), no de users/{uid}; ilegible → no se ajusta. Encender o apagar los
 * permisos de eliminar exige rango ≥ 60: para el resto esas casillas salen
 * deshabilitadas con el motivo (lib/matrix_delete_lock) y, si el usuario aún no
 * tiene matriz, se guardan apagadas. Las reglas de Firestore exigen lo mismo.
 *
 * «Restablecer a los permisos del rol»: cliente / admin / con alcance / otro
 * rol → llena el formulario con la siembra (se aplica con «Guardar»);
 * personal global → borra su matriz de reducción (vuelve a su rol), con
 * confirmación y auditoría.
 *
 * Ruling R2: si la matriz no se puede leer, no se supone «no hay»: se avisa y
 * no se deja guardar. Si el rol no se puede leer, sus permisos por defecto son
 * DESCONOCIDOS: no se muestran como si fueran los del rol y «Restablecer» queda
 * bloqueado.
 */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFirebase } from 'react-redux-firebase';
import { Drawer, Form, Button, Spin, notification, Alert, Modal, Tooltip } from 'antd';

import { documents as documentsActions } from '../../actions/database/documents';
import { PermissionMatrix, matrixToFields, fieldsToPermissions } from '../documents/permission_matrix';
import { seedMatrixFor, defaultCompanyPerms, userKind } from '../../lib/role_seed';
import { companyMatrixPath } from '../../lib/folder_chain';
import { canAdjust, canToggleDelete } from '../../lib/rank';
import { loadTargetRole, targetRoleOf } from '../../lib/target_rank';
import { deleteLockedMap, keepDeleteFields } from '../../lib/matrix_delete_lock';
import { globalCarpetas, companyCarpetasToSave } from '../../lib/closed_folder_perms';

// carpetasGlobal: abrir/cerrar del objetivo por rol + users/{uid}.Carpetas
// (solo lo carga root; null = no aplica o no se pudo leer).
const INITIAL = { role: null, targetRole: null, kind: null, hasMatrix: false, readError: false, roleUnknown: false, loaded: false, carpetasGlobal: null };

// Campos de «Abrir y cerrar carpetas»: lo que decidió la empresa, o lo global.
const carpetasFields = (company, global) => ['abrir', 'cerrar'].map((k) => ({
  name: `carpetas_${k}`,
  value: typeof company?.[k] === 'boolean' ? company[k] : global[k] === true,
}));

export const CustomerPermissionsDrawer = ({ open, customerId, customerName, user, onClose, zIndex }) => {

  const dispatch = useDispatch();
  const firebase = useFirebase();
  const roles = useSelector(state => state.get('roles').get('roles').roles);

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [target, setTarget] = useState(INITIAL);

  const uid = user?.reference?.id;
  const isAdmin = user?.isAdmin === true;
  const canDelete = canToggleDelete(roles);
  const editorIsRoot = roles?.documents?.root === true;
  const matrixRef = () => firebase.firestore().doc(companyMatrixPath(customerId, uid));

  // Carga la matriz existente (o el valor por defecto del rol) al abrir.
  useEffect(() => {
    if (!open || !customerId || !uid) return;
    setLoading(true);
    setTarget(INITIAL);
    form.resetFields();
    const db = firebase.firestore();
    // El rol del objetivo sale del espejo público (nunca users/{uid}); null = ilegible.
    // Root además lee users/{uid}.Carpetas (abrir/cerrar del usuario); si falla,
    // la sección no se muestra (no se adivina lo global).
    const userRead = editorIsRoot ? db.doc(`users/${uid}`).get().catch(() => null) : Promise.resolve(null);
    Promise.all([db.doc(companyMatrixPath(customerId, uid)).get(), loadTargetRole({ db, uid }), userRead])
      .then(([snap, role, userSnap]) => {
        const data = snap.exists ? snap.data() : null;
        // Admin de empresa: su defecto no depende del rol leído.
        const roleUnknown = !role && !isAdmin;
        const kind = roleUnknown ? null : userKind({ role: role || {}, isAdmin });
        if (data?.permissions) form.setFields(matrixToFields(data.permissions));
        else if (!roleUnknown) {
          // Sin matriz propia, quien no puede dar eliminar no los guarda encendidos.
          form.setFields(keepDeleteFields({
            fields: matrixToFields(defaultCompanyPerms({ role: role || {}, isAdmin })),
            current: {},
            canToggle: canDelete,
          }));
        }
        let carpetasGlobal = null;
        if (editorIsRoot && role && userSnap) {
          const own = (userSnap.exists && userSnap.data()?.Carpetas) || {};
          carpetasGlobal = globalCarpetas({ ...role, carpetas: { abrir: own.abrir === true, cerrar: own.cerrar === true } });
          form.setFields(carpetasFields(data?.permissions?.carpetas, carpetasGlobal));
        }
        setTarget({ role, targetRole: targetRoleOf(role), kind, hasMatrix: !!data, readError: false, roleUnknown, loaded: true, carpetasGlobal });
        setLoading(false);
      })
      .catch(() => {
        setTarget({ ...INITIAL, readError: true });
        setLoading(false);
      });
  }, [open, customerId, uid, isAdmin, firebase, form, canDelete, editorIsRoot]);

  const done = (msg) => {
    setLoading(false);
    notification.success({ message: 'Permisos del usuario', description: msg, placement: 'topLeft' });
    onClose();
  };
  // Los rechazos por rango traen un mensaje claro: se muestra tal cual.
  const failed = (error) => {
    setLoading(false);
    notification.error({
      message: 'Permisos del usuario',
      description: (error && error.message) || 'Se presentó un error al actualizar los permisos.',
      placement: 'topLeft',
    });
  };

  const adjustBlocked = !!open && !loading && target.loaded && !target.readError && !canAdjust(roles, target.targetRole);

  const onSave = values => {
    if (target.readError || adjustBlocked) return;
    setLoading(true);
    dispatch(documentsActions.setPermissions({
      roles: roles.folders,
      matrixRef: matrixRef(),
      permissions: fieldsToPermissions(values),
      // Solo lo que difiere de lo global queda como decisión de la empresa.
      ...(target.carpetasGlobal ? {
        carpetas: companyCarpetasToSave({
          values: { abrir: values.carpetas_abrir, cerrar: values.carpetas_cerrar },
          global: target.carpetasGlobal,
        }),
      } : {}),
      uid,
      editorRole: roles,
      targetRole: target.targetRole,
    })).then(() => done(`Se actualizaron los permisos de ${user?.email}.`)).catch(failed);
  };

  const onReset = () => {
    if (target.roleUnknown || target.readError || adjustBlocked) return;
    const seed = seedMatrixFor({ role: target.role || {}, isAdmin });
    if (seed) {
      form.setFields(keepDeleteFields({ fields: matrixToFields(seed), current: form.getFieldsValue(), canToggle: canDelete }));
      if (target.carpetasGlobal) form.setFields(carpetasFields(null, target.carpetasGlobal));
      notification.info({
        message: 'Permisos del rol',
        description: canDelete
          ? 'Se cargaron los permisos del rol. Guarde para aplicarlos.'
          : 'Se cargaron los permisos del rol, salvo los de eliminar, que conservan su valor. Guarde para aplicarlos.',
        placement: 'topLeft',
      });
      return;
    }
    if (!target.hasMatrix) {
      notification.info({ message: 'Permisos del rol', description: 'Ya tiene los permisos de su rol en esta empresa.', placement: 'topLeft' });
      return;
    }
    Modal.confirm({
      title: 'Restablecer a los permisos del rol',
      content: 'Se borra la reducción de esta empresa y vuelve a tener los permisos de su rol. ¿Continuar?',
      okText: 'Restablecer', cancelText: 'Cancelar',
      onOk: () => {
        setLoading(true);
        return dispatch(documentsActions.clearCompanyPermissions({
          roles: roles.folders, matrixRef: matrixRef(), uid, editorRole: roles, targetRole: target.targetRole,
        })).then(() => done(`${user?.email} vuelve a tener los permisos de su rol.`)).catch(failed);
      },
    });
  };

  const isStaff = target.kind === 'staff' || (target.roleUnknown && user?.staff === true);
  const resetBlocked = target.roleUnknown || target.readError || adjustBlocked;
  const saveBlocked = target.readError || (target.roleUnknown && !target.hasMatrix) || adjustBlocked;

  return (
    <Drawer
      forceRender
      title={`Permisos de ${user?.email || ''}${customerName ? ` — ${customerName}` : ''}`}
      width={720}
      placement="left"
      onClose={onClose}
      maskClosable={false}
      visible={open}
      zIndex={zIndex}
      style={{ height: 'calc(100% - 55px)', overflow: 'auto', paddingBottom: 53 }}
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Aplican a todas las carpetas de la empresa, salvo las que lo restrinjan."
        description="Valen para este usuario en toda la empresa y pueden quedar por debajo o por encima de los de su rol."
      />
      {adjustBlocked ?
        <Alert type="error" showIcon style={{ marginBottom: 16 }}
          message="No puede ajustar a este usuario: su rango es igual o mayor al suyo, o no se pudo leer su rol." /> : null}
      {!canDelete && !loading ?
        <Alert type="info" showIcon style={{ marginBottom: 16 }}
          message="Los permisos de eliminar están bloqueados: solo contador, supervisora o root pueden darlos o quitarlos."
          description={!target.hasMatrix && !target.readError ? 'Como este usuario aún no tiene permisos propios, al guardar los de eliminar quedarán apagados.' : undefined} /> : null}
      {target.readError && !loading ?
        <Alert type="error" showIcon style={{ marginBottom: 16 }}
          message="No se pudieron leer los permisos actuales de este usuario en la empresa. Cierre e intente de nuevo; no se guardará nada hasta poder leerlos." /> : null}
      {target.roleUnknown && !loading ?
        <Alert type="warning" showIcon style={{ marginBottom: 16 }}
          message={target.hasMatrix
            ? 'No se pudo leer el rol de este usuario: puede editar sus permisos en la empresa, pero no restablecerlos a los del rol.'
            : 'No se pudo leer el rol de este usuario y no tiene permisos propios en la empresa: no se pueden mostrar los que tiene por defecto ni guardar.'} /> : null}
      {!target.hasMatrix && !target.readError && !target.roleUnknown && !loading ?
        <Alert type="warning" showIcon style={{ marginBottom: 16 }}
          message="No tiene permisos propios en esta empresa: se muestran los de su rol. Al guardar, quedarán fijos para esta empresa." /> : null}
      {isStaff && !loading ?
        <Alert type="warning" showIcon style={{ marginBottom: 16 }}
          message="Es personal global: esta matriz solo ajusta sus permisos en esta empresa y no lo vincula como usuario. Ojo: si después se le pone «Solo sus empresas», esta matriz contará como pertenencia a la empresa." /> : null}
      <Spin spinning={loading}>
        <Form form={form} layout="vertical" onFinish={onSave}>
          <PermissionMatrix locked={deleteLockedMap(canDelete)}
            carpetas={target.carpetasGlobal ? { global: target.carpetasGlobal } : null} />
        </Form>
      </Spin>
      <div style={{
        position: 'absolute', bottom: 0, width: '100%', borderTop: '1px solid #e8e8e8',
        padding: '10px 16px', textAlign: 'right', left: 0, background: '#fff', borderRadius: '0 0 4px 4px',
      }}>
        <span style={{ float: 'left' }}>
          <Tooltip title={resetBlocked ? 'No se puede: no se pudo leer el rol o los permisos de este usuario, o no puede ajustarlo.' : ''}>
            <Button loading={loading} disabled={resetBlocked} onClick={onReset}>Restablecer a los permisos del rol</Button>
          </Tooltip>
        </span>
        <Button style={{ marginRight: 8 }} loading={loading} onClick={onClose}>Cancelar</Button>
        <Button type="primary" htmlType="submit" onClick={() => form.submit()} loading={loading} disabled={saveBlocked}>Guardar permisos</Button>
      </div>
    </Drawer>
  );

};

export default CustomerPermissionsDrawer;
